/**
 * DRBFM Custom Hooks
 *
 * React hooks for managing DRBFM analysis workflow:
 * - useDRBFM: Main hook for full DRBFM workflow
 * - useDRBFMSessions: Hook for session list management
 * - useDRBFMAnalysis: Hook for analysis execution with SSE
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from './redux';
import {
  // Selectors
  selectSessions,
  selectCurrentSession,
  selectSessionsLoading,
  selectSessionsError,
  selectWizardStep,
  selectInputData,
  selectAnalysisProgress,
  selectAnalysisResults,
  selectIsExporting,
  selectExportError,
  selectCanStartAnalysis,
  // Actions
  setCurrentSession,
  setWizardStep,
  setInputData,
  startAnalysis,
  setCurrentFlow,
  setOverallProgress,
  updateFlowStatus,
  addAnalysisMessage,
  setAnalysisError,
  completeAnalysis,
  cancelAnalysis,
  setAnalysisResults,
  resetWizard,
  startExport,
  completeExport,
  setExportError,
  clearSessionsError,
} from '../features/drbfm/drbfmSlice';
import {
  drbfmService,
  DRBFMSSEClient,
  CreateSessionParams,
  startAnalysisWithFetch,
} from '../services/drbfm.service';
import {
  DRBFMSession,
  DRBFMInputData,
  DRBFMWizardStep,
  DRBFMProgressEvent,
  FlowProgress,
  PredefinedPartLabel,
} from '../types';

// ==============================================================================
// useDRBFMSessions - Session List Management
// ==============================================================================

export const useDRBFMSessions = () => {
  const dispatch = useAppDispatch();
  const sessions = useAppSelector(selectSessions);
  const isLoading = useAppSelector(selectSessionsLoading);
  const error = useAppSelector(selectSessionsError);

  const [localSessions, setLocalSessions] = useState<DRBFMSession[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Fetch sessions from API
  const fetchSessions = useCallback(async (limit = 20, offset = 0) => {
    setLocalLoading(true);
    setLocalError(null);

    try {
      const response = await drbfmService.listSessions({ limit, offset });
      setLocalSessions(response.sessions);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sessions';
      setLocalError(errorMessage);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  }, []);

  // Delete a session
  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await drbfmService.deleteSession(sessionId);
      setLocalSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete session';
      setLocalError(errorMessage);
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setLocalError(null);
    dispatch(clearSessionsError());
  }, [dispatch]);

  return {
    sessions: localSessions.length > 0 ? localSessions : sessions,
    isLoading: localLoading || isLoading,
    error: localError || error,
    fetchSessions,
    deleteSession,
    clearError,
  };
};

// ==============================================================================
// useDRBFMAnalysis - Analysis Execution with SSE
// ==============================================================================

export const useDRBFMAnalysis = () => {
  const dispatch = useAppDispatch();
  const progress = useAppSelector(selectAnalysisProgress);
  const results = useAppSelector(selectAnalysisResults);

  const sseClientRef = useRef<DRBFMSSEClient | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // Start analysis with SSE streaming
  const runAnalysis = useCallback(
    async (
      sessionId: string,
      oldSpecification: string,
      newSpecification: string,
      cadModelId?: string,
      predefinedPartLabels?: PredefinedPartLabel[],
    ) => {
      // Dispatch start action
      dispatch(startAnalysis());
      setIsRunning(true);

      // Create AbortController for cancellation
      abortControllerRef.current = new AbortController();

      // Use fetch-based SSE for large specifications (POST request)
      try {
        await startAnalysisWithFetch(
          sessionId,
          { oldSpecification, newSpecification, cadModelId, predefinedPartLabels },
          {
            onProgress: (event: DRBFMProgressEvent) => {
              // Use atomic actions to avoid stale closure issues
              // Each action directly updates the Redux state without relying on captured values
              const flowKey = `flow${event.flow}` as keyof FlowProgress;
              let status: FlowProgress[keyof FlowProgress] = 'in_progress';

              if (event.type === 'flow_complete' || event.type === 'cad_link_complete') {
                status = 'completed';
              } else if (event.type === 'error' || event.type === 'cad_link_failed') {
                status = 'failed';
              }

              // Dispatch atomic updates instead of constructing full state
              dispatch(setCurrentFlow(event.flow));
              dispatch(setOverallProgress(event.progress));
              dispatch(updateFlowStatus({ flow: flowKey, status }));
              dispatch(addAnalysisMessage(event.message));

              if (event.type === 'cad_link_failed') {
                dispatch(setAnalysisError(event.message));
              } else if (
                event.type === 'cad_link_complete'
                && event.data?.unresolvedFailureModeIds
                && event.data.unresolvedFailureModeIds.length > 0
              ) {
                dispatch(setAnalysisError(event.message));
              }
            },
            onComplete: async () => {
              dispatch(completeAnalysis());
              setIsRunning(false);

              // Fetch results after completion
              try {
                const results = await drbfmService.getSessionResults(sessionId);
                dispatch(setAnalysisResults(results));
              } catch (err) {
                console.error('Failed to fetch results:', err);
              }
            },
            onError: (error: string) => {
              dispatch(setAnalysisError(error));
              setIsRunning(false);
            },
          },
          abortControllerRef.current.signal,
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
        dispatch(setAnalysisError(errorMessage));
        setIsRunning(false);
      }
    },
    [dispatch]
  );

  // Cancel analysis
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (sseClientRef.current) {
      sseClientRef.current.disconnect();
      sseClientRef.current = null;
    }
    dispatch(cancelAnalysis());
    setIsRunning(false);
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sseClientRef.current) {
        sseClientRef.current.disconnect();
      }
    };
  }, []);

  return {
    progress,
    results,
    isRunning,
    runAnalysis,
    cancel,
  };
};

// ==============================================================================
// useDRBFM - Main Hook for Full DRBFM Workflow
// ==============================================================================

export const useDRBFM = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Selectors
  const currentSession = useAppSelector(selectCurrentSession);
  const wizardStep = useAppSelector(selectWizardStep);
  const inputData = useAppSelector(selectInputData);
  const analysisProgress = useAppSelector(selectAnalysisProgress);
  const analysisResults = useAppSelector(selectAnalysisResults);
  const isExporting = useAppSelector(selectIsExporting);
  const exportError = useAppSelector(selectExportError);
  const canStartAnalysis = useAppSelector(selectCanStartAnalysis);
  const sessionsLoading = useAppSelector(selectSessionsLoading);
  const sessionsError = useAppSelector(selectSessionsError);

  // Local state
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Use sub-hooks
  const { runAnalysis, cancel: cancelAnalysis, isRunning } = useDRBFMAnalysis();

  // Create a new session and start analysis
  const createSessionAndAnalyze = useCallback(async () => {
    if (!user) {
      dispatch(setAnalysisError('Authentication required'));
      return null;
    }

    if (!canStartAnalysis) {
      dispatch(setAnalysisError('Please fill in all required fields'));
      return null;
    }

    setIsCreatingSession(true);

    try {
      // Create session
      const params: CreateSessionParams = {
        itemName: inputData.itemName,
        title: inputData.title,
        specOldDocumentId: inputData.specOldDocumentId,
        specNewDocumentId: inputData.specNewDocumentId,
      };

      const response = await drbfmService.createSession(params);

      // Get full session details
      const session = await drbfmService.getSession(response.id);
      dispatch(setCurrentSession(session));

      // Start analysis
      await runAnalysis(
        session.id,
        inputData.oldSpecification,
        inputData.newSpecification,
        inputData.cadModelId,
        inputData.predefinedPartLabels,
      );

      return session;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create session';
      dispatch(setAnalysisError(errorMessage));
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  }, [user, canStartAnalysis, inputData, dispatch, runAnalysis]);

  // Load an existing session
  const loadSession = useCallback(
    async (sessionId: string) => {
      try {
        const session = await drbfmService.getSession(sessionId);
        dispatch(setCurrentSession(session));

        // If session is completed, load results
        if (session.status === 'completed') {
          const results = await drbfmService.getSessionResults(sessionId);
          dispatch(setAnalysisResults(results));
          dispatch(setWizardStep('results'));
        } else if (session.status === 'draft') {
          dispatch(setWizardStep('input'));
        }

        return session;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load session';
        dispatch(setAnalysisError(errorMessage));
        return null;
      }
    },
    [dispatch]
  );

  // Resume analysis for an existing session
  const resumeAnalysis = useCallback(
    async (sessionId: string) => {
      if (!inputData.oldSpecification || !inputData.newSpecification) {
        dispatch(setAnalysisError('Specifications required to resume analysis'));
        return;
      }

      await runAnalysis(
        sessionId,
        inputData.oldSpecification,
        inputData.newSpecification,
        inputData.cadModelId,
        inputData.predefinedPartLabels,
      );
    },
    [inputData, runAnalysis, dispatch]
  );

  // Update input data
  const updateInput = useCallback(
    (data: Partial<DRBFMInputData>) => {
      dispatch(setInputData(data));
    },
    [dispatch]
  );

  // Navigate to wizard step
  const goToStep = useCallback(
    (step: DRBFMWizardStep) => {
      dispatch(setWizardStep(step));
    },
    [dispatch]
  );

  // Reset the wizard
  const reset = useCallback(() => {
    dispatch(resetWizard());
  }, [dispatch]);

  // Export results as HTML
  const exportHTML = useCallback(async () => {
    if (!currentSession) {
      dispatch(setExportError('No session to export'));
      return;
    }

    dispatch(startExport());

    try {
      const filename = `drbfm_${currentSession.itemName}_${currentSession.id.slice(0, 8)}.html`;
      await drbfmService.downloadExportedHTML(currentSession.id, filename);
      dispatch(completeExport());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Export failed';
      dispatch(setExportError(errorMessage));
    }
  }, [currentSession, dispatch]);

  return {
    // State
    currentSession,
    wizardStep,
    inputData,
    analysisProgress,
    analysisResults,
    isExporting,
    exportError,
    canStartAnalysis,
    isLoading: sessionsLoading || isCreatingSession,
    isAnalyzing: isRunning || analysisProgress.isRunning,
    error: sessionsError || analysisProgress.error,

    // Actions
    createSessionAndAnalyze,
    loadSession,
    resumeAnalysis,
    cancelAnalysis,
    updateInput,
    goToStep,
    reset,
    exportHTML,
  };
};

// ==============================================================================
// Default Export
// ==============================================================================

export default useDRBFM;
